import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { IBuffetDishDto } from '../../core/api/dto/BuffetDishDto'
import { useGetBuffetDishQuery } from '../../core/api/BuffetDishApi'
import Checkbox from '../../components/checkbox/Checkbox'
import { useTranslation } from 'react-i18next'
import Spinner, { SpinnerColor, SpinnerPosition, SpinnerSize } from '../../components/spinner/Spinner'

interface IAppWidgetDishProps {
  title: string
  categoryId: number
  dishesByCategoryValue?: IBuffetDishDto[]
  dishesValue: IBuffetDishDto[]
  setDishesValue: React.Dispatch<React.SetStateAction<IBuffetDishDto[]>>
  sourceId?: number
}

export const AppWidgetDish = ({
  title,
  categoryId,
  dishesValue,
  setDishesValue,
  dishesByCategoryValue,
  sourceId,
}: IAppWidgetDishProps) => {
  const { t } = useTranslation()
  const [active, setActive] = useState<boolean>(!!dishesByCategoryValue)
  const [chooseAll, setChooseAll] = useState(false)
  const [dishOffset, setDishOffset] = useState(0)
  const [loadMoreDishes, setLoadMoreDishes] = useState(false)
  const [accumulatedDishes, setAccumulatedDishes] = useState<IBuffetDishDto[]>([])

  const { data: buffetDishesList, isLoading, isFetching } = useGetBuffetDishQuery(
    { limit: 40, offset: dishOffset, category_ids: `${categoryId}`, is_archived: false },
    { skip: !!dishesByCategoryValue || !active || !categoryId },
  )

  useEffect(() => {
    if (!buffetDishesList) return
    if (dishOffset === 0) {
      setAccumulatedDishes(buffetDishesList.results)
    } else if (loadMoreDishes) {
      setAccumulatedDishes((prev) => [...prev, ...buffetDishesList.results])
      setLoadMoreDishes(false)
    }
  }, [buffetDishesList])

  const onChangeHandler = (newItem: IBuffetDishDto) => {
    const duplicate = dishesValue.find((item) => item.id === newItem.id)
    if (!!duplicate) {
      const removeDuplicate = dishesValue.filter(
        (item) => item.id !== duplicate.id,
      )
      setDishesValue(removeDuplicate)
    } else setDishesValue([...dishesValue, newItem])
  }

  const onToggleHandler = useCallback(
    () => setActive((prevState) => !prevState),
    [],
  )

  const showMoreDishesHandler = () => {
    if (buffetDishesList?.next) {
      const url = new URL(buffetDishesList.next)
      const newOffset = url.searchParams.get('offset')
      if (newOffset) {
        setLoadMoreDishes(true)
        setDishOffset(Number(newOffset))
      }
    }
  }

  const dishArr = useMemo(() => {
    return dishesByCategoryValue || accumulatedDishes
  }, [dishesByCategoryValue, accumulatedDishes])

  const setChooseAllHandler = (value: boolean) => {
    setChooseAll(value)
    if (value) {
      setDishesValue((prevState) =>
        prevState
          .concat(dishArr)
          .filter(
            (obj, index, self) =>
              index ===
              self.findIndex((t) => t.id === obj.id && t.name === obj.name),
          ),
      )
    }
    if (!value) {
      setDishesValue((prevState) =>
        prevState.filter(
          (obj1) => !dishArr.some((obj2) => obj1.id === obj2.id),
        ),
      )
    }
  }

  return (
    <div className="widget-dish-wrap">
      <div className={`title-block ${active ? 'active' : ''}`}>
        <div className="title">{t(title)}</div>
        <div className="toggle" onClick={onToggleHandler}>
          <i className="an-ico an-ico-arrow-down-bold" />
        </div>
      </div>
      {active && (
        <div className="widget-dish-content">
          <React.Fragment>
            {!!dishArr.length && (
              <Checkbox
                name={`${categoryId}`}
                onChange={setChooseAllHandler}
                value={chooseAll}
                text="Выбрать все"
              />
            )}
            {isLoading ? (
              <p>{t('Загрузка')}</p>
            ) : dishArr.length > 0 ? (
              dishArr.map((dish, index) => {
                const isChoose = dishesValue.find(
                  (dishValue) => dishValue.id === dish.id,
                )
                return (
                  <Checkbox
                    name={dish.name}
                    text={dish.name}
                    value={!!isChoose}
                    onChange={() => onChangeHandler(dish as IBuffetDishDto)}
                    key={`${dish.id}-i-${index}`}
                  />
                )
              })
            ) : null}

            {!!buffetDishesList?.next && (
              isFetching
                ? <Spinner color={SpinnerColor.violet} size={SpinnerSize.sm} spinnerPosition={SpinnerPosition.center} />
                : <div className="btn md violet-white" onClick={showMoreDishesHandler}>{t('Показать ещё')}</div>
            )}
          </React.Fragment>
        </div>
      )}
    </div>
  )
}
